import { instanceOfICheckableObject, instanceOfICheckObject, instanceOfIComboObject, instanceOfIDateObject, instanceOfITextObject } from "../../../../domain/editableFormat/objects";
import { DomainRegistry } from "../../../../domain";
import { ComparisonUtility, IEditableFormatObject, IEditableFormatPageStructure, IEditableFormatStructure } from "../../../../domain/editableFormat";
import { IFormatObjectBean } from "../beans";
import { Translator } from "./Translator";
import { instanceOfIQuestionnaireSpecifiable, TextObjectKinds } from "../../../../../documaker/domain/format/objects";
import { NewObjectKeys } from "../../../../domain/configuration";
import { instanceOfIRectangleCoordinatable, instanceOfISizeSpecifiable, ITextAreaStyle } from "../../../../../../domain/drawing";
import { ObjectUtility } from "../../../../../../utilities";
import { QuestionnaireGuidTree } from "../../../../domain/questionnaire";

export class QuestionnaireExtendedTranslator extends Translator {
  constructor() {
    super();
  }

  toFormatStructure(source: IFormatObjectBean): IEditableFormatStructure {
    const result = super.toFormatStructure(source);
    result.pages.forEach(p => p.objects.sort((a, b) => ComparisonUtility.compareUpperLeftToRightBottom(a, b)));
    const objects = result.pages.reduce((p, c) => p.concat(c.objects.filter(v => !!this.toNewObjectKey(v)).map(v => this.setInitialRectangle(v))), [] as IEditableFormatObject[]);
    const page = result.pages[0];
    page.objects = QuestionnaireGuidTree.sort(QuestionnaireGuidTree.from(objects)).map(o => o.source);
    result.pages = [page];
    return result;
  }

  private toNewObjectKey(object: IEditableFormatObject): NewObjectKeys {
    if (instanceOfIQuestionnaireSpecifiable(object) && !!object.questionnaire.linkId) {
      if (instanceOfITextObject(object)) {
        if (object.objectKind === TextObjectKinds.Free) {
          return "NewFreeText";
        } else if (object.objectKind === TextObjectKinds.Fixed) {
          return "NewFixedText";
        } else if (object.objectKind === TextObjectKinds.Number) {
          return "NewNumberText";
        }
      } else if (instanceOfIDateObject(object)) {
        if (object.isDate) {
          return "NewDate";
        } else {
          return "NewTime";
        }
      } else if (instanceOfICheckObject(object)) {
        return "NewCheckBox";
      } else if (instanceOfIComboObject(object)) {
        return "NewComboBox";
      }
    }
    return null;
  }

  private setInitialRectangle(object: IEditableFormatObject): IEditableFormatObject {
    const newObject = this.createNewObject(object);
    if (instanceOfIRectangleCoordinatable(object) && instanceOfIRectangleCoordinatable(newObject)) {
      object.location = newObject.location;
      object.size = newObject.size;
    }
    return object;
  }

  private createNewObject(object: IEditableFormatObject): IEditableFormatObject {
    const key = this.toNewObjectKey(object);
    return key
      ? DomainRegistry.instance.editableFormat.getFormatRepository().createNewFormatObject(key, null)
      : null;
  }

  toFormatObjectBean(source: IEditableFormatStructure, isTemporary: boolean): IFormatObjectBean {
    const converter = new QuestionnaireEditableFormatStructureConverter(source);
    return super.toFormatObjectBean(converter.toSource(), isTemporary);
  }
}

class QuestionnaireEditableFormatStructureConverter {
  private readonly _gap = 10;

  constructor(source: IEditableFormatStructure) {
    this.baseSource = ObjectUtility.copyAtomicDeeply(source);
    this.basePage = ObjectUtility.copyAtomicDeeply(this.baseSource.pages[0]);
    this.allObjects = this.basePage.objects;
    this.basePage.objects = [];
    this.pages = [];
    this.convert();
  }

  private baseSource: IEditableFormatStructure;
  private basePage: IEditableFormatPageStructure;
  private allObjects: IEditableFormatObject[];
  private pages: IEditableFormatPageStructure[];
  private page: IEditableFormatPageStructure;
  private x: number;
  private y: number;
  private objects: IEditableFormatObject[];

  private convert() {
    this.newPage();
    this.allObjects.forEach(o => {
      if (this.shouldNextPage(o)) {
        this.addPage();
        this.newPage();
      }
      this.addObject(o);
      this.x = 0;
      this.y = this.getNextPositionY(o);
    });
    this.addPage();
    this.fixPage();
  }

  private newPage() {
    this.page = ObjectUtility.copyAtomicDeeply(this.basePage);
    this.page.pageKey = this.pages.length + 1;
    this.x = 0;
    this.y = this._gap;
    this.objects = [];
  }

  private shouldNextPage(object: IEditableFormatObject): boolean {
    return this.page.image.size.height <= this.getNextPositionY(object);
  }

  private getNextPositionY(object: IEditableFormatObject): number {
    return this.y + this.toBlockHeight(object);
  }

  private toBlockHeight(object: IEditableFormatObject): number {
    return instanceOfIRectangleCoordinatable(object)
      ? object.size.height + this._gap
      : 0;
  }

  private addObject(object: IEditableFormatObject) {
    if (instanceOfIQuestionnaireSpecifiable(object)) {
      switch (object.questionnaire.type) {
        case "display":
        case "group":
          this.addTitleObjects(object);
          return;
        case "string":
        case "text":
          this.addFreeTextObjects(object);
          return;
        case "integer":
        case "quantity":
        case "decimal":
          this.addNumInputObjects(object);
          return;
        case "choice":
          this.addChoiceObjects(object);
          return;
        case "date":
        case "dateTime":
          this.addDateObjects(object);
          return;
      }
    }
    this.addBasicObject(object);
  }

  private addTitleObjects(object: IEditableFormatObject) {
    this.addBasicObject(object);
  }

  private addFreeTextObjects(object: IEditableFormatObject) {
    this.addRelatedTitleObject(object);
    this.addBasicObject(object);
  }

  private addNumInputObjects(object: IEditableFormatObject) {
    this.addRelatedTitleObject(object);
    this.addBasicObject(object);
    this.addUnitObject(object);
  }

  private addChoiceObjects(object: IEditableFormatObject) {
    switch (object.elementName) {
      case "check":
        this.addBasicObject(object);
        this.addCheckTextObject(object);
        return;
      case "combo":
        this.addRelatedTitleObject(object);
        this.addBasicObject(object);
        return;
    }
  }

  private addDateObjects(object: IEditableFormatObject) {
    this.addRelatedTitleObject(object);
    this.addBasicObject(object);
  }

  private addRelatedTitleObject(object: IEditableFormatObject) {
    const fixedObject = DomainRegistry.instance.editableFormat.getFormatRepository().createNewFormatObject("NewFixedText", null);
    if (!instanceOfIQuestionnaireSpecifiable(object) ||
      !instanceOfISizeSpecifiable(object) ||
      !this.instanceOfITextAreaStyle(object) ||
      !instanceOfITextObject(fixedObject)) {
      return;
    }
    const text = !!object.questionnaire.text ? object.questionnaire.text : "";
    fixedObject.questionnaire._guid = object.questionnaire._guid;
    fixedObject.questionnaire.type = undefined;
    fixedObject.text = text;
    fixedObject.location.x = this.x;
    fixedObject.location.y = this.y;
    fixedObject.size.width = fixedObject.textStyle.fontStyle.font.size * text.length;
    fixedObject.size.height = object.size.height;
    this.objects.push(fixedObject);
    this.x += fixedObject.size.width;
  }

  private instanceOfITextAreaStyle(instance: any): instance is { textStyle: ITextAreaStyle } {
    return instance && instance.textStyle;
  }

  private addUnitObject(object: IEditableFormatObject) {
    // TODO: quantity対応時に修正
  }

  private addCheckTextObject(object: IEditableFormatObject) {
    const fixedObject = DomainRegistry.instance.editableFormat.getFormatRepository().createNewFormatObject("NewFixedText", null);
    if (!instanceOfIQuestionnaireSpecifiable(object) ||
      !instanceOfICheckableObject(object, object.elementName) ||
      !instanceOfITextObject(fixedObject)) {
      return;
    }
    const text = !!object.categoryName ? object.categoryName : "";
    fixedObject.questionnaire._guid = object.questionnaire._guid;
    fixedObject.questionnaire.type = undefined;
    fixedObject.text = text;
    fixedObject.location.x = this.x;
    fixedObject.location.y = this.y;
    fixedObject.size.width = fixedObject.textStyle.fontStyle.font.size * text.length;
    fixedObject.size.height = object.size.height;
    this.objects.push(fixedObject);
    this.x += fixedObject.size.width;
  }

  private addBasicObject(object: IEditableFormatObject) {
    if (!instanceOfIRectangleCoordinatable(object)) {
      return;
    }
    this.setCorrectPosition(object);
    this.objects.push(object);
    this.x += object.size.width;
  }

  private setCorrectPosition(object: IEditableFormatObject) {
    if (!instanceOfIRectangleCoordinatable(object)) {
      return;
    }
    object.location.x = this.x;
    object.location.y = this.y;
  }

  private addPage() {
    this.page.objects = this.objects;
    this.pages.push(this.page);
  }

  private fixPage() {
    this.baseSource.pages = this.pages;
    this.baseSource.maxPageKey = this.pages.length;
  }

  toSource(): IEditableFormatStructure {
    return this.baseSource;
  }
}
